import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import feather from "../resources/icons/feather-pointed 1.svg";

const PortalActivity = ({
  portals,
  initialInterval = 1000,
  startCycle = false,
  onPortalChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [intervalMs, setIntervalMs] = useState(initialInterval);
  const [isPaused, setIsPaused] = useState(false);
  const [isComponentVisible, setIsComponentVisible] = useState(false);
  const [cycleStarted, setCycleStarted] = useState(false);
  const [cycleCompleted, setCycleCompleted] = useState(false);
  const [prevStartCycle, setPrevStartCycle] = useState(false);

  const portalsArray = Array.isArray(portals) ? portals : Object.values(portals);
  const currentPortal = portalsArray.length > 0 ? portalsArray[currentIndex] : null;

  const updatePortalIndex = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === portalsArray.length - 1) {
        setIsComponentVisible(false);
        setCycleStarted(false);
        setCycleCompleted(true);
        if (onPortalChange) {
          onPortalChange(currentPortal || {}, 0, true);
        }
        return 0;
      }
      return prevIndex + 1;
    });
  };

  useEffect(() => {
    if (isComponentVisible && currentPortal && onPortalChange) {
      const message = currentPortal.Message || "0 new orders";
      const newOrders = parseInt(message.match(/\d+/)?.[0] || "0", 10);
      const timer = setTimeout(() => {
        onPortalChange(currentPortal, newOrders, false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, isComponentVisible, currentPortal, onPortalChange]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCurrentIndex(0);
        setCycleStarted(false);
        setIsComponentVisible(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (cycleStarted && portalsArray.length === 1) {
      const timer = setTimeout(() => {
        setIsComponentVisible(false);
        setCycleStarted(false);
        setCycleCompleted(true);
        setCurrentIndex(0);
        if (onPortalChange && currentPortal) {
          onPortalChange(currentPortal, 0, true);
        }
      }, intervalMs);
      return () => clearTimeout(timer);
    }
  }, [cycleStarted, portalsArray.length, intervalMs, currentPortal, onPortalChange]);

  useEffect(() => {
    if (startCycle && !prevStartCycle) {
      setCurrentIndex(0);
      setCycleCompleted(false);
      setCycleStarted(true);
      setIsComponentVisible(true);
    }
    setPrevStartCycle(startCycle);
  }, [startCycle, prevStartCycle]);

  useEffect(() => {
    if (!cycleStarted || isPaused || portalsArray.length <= 1) return;
    const timer = setInterval(updatePortalIndex, intervalMs);
    return () => clearInterval(timer);
  }, [cycleStarted, intervalMs, isPaused, portalsArray.length]);

  if (!currentPortal) {
    return (
      <div
        style={{
          color: "rgba(255, 255, 255)",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "48px",
          fontWeight: "500",
          lineHeight: "110%",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          letterSpacing: "0.88px",
        }}
      >
        <img src={feather} alt="Feather icon" style={{ fill: "#FFFFFF" }} width="42" height="42" />
        <span>No portals configured</span>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {isComponentVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            maxWidth: "940px",
            position: "relative",
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              top: "-42px",
              position: "absolute",
              left: "0",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 6px",
              background: "rgba(255, 255, 255, 0.16)",
              backdropFilter: "blur(8px)",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            <AnimatePresence mode="wait">
              {currentPortal.logo && (
                <motion.img
                  key={currentPortal.logo}
                  src={currentPortal.logo}
                  alt={`${currentPortal.display || currentPortal.name} Logo`}
                  width="12"
                  height="12"
                  initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 1.2, rotate: 10 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              )}
            </AnimatePresence>
            <span
              style={{
                color: "#FFFFFF",
                fontSize: "11px",
                fontWeight: "500",
                letterSpacing: "0.88px",
              }}
            >
              {currentPortal.display || currentPortal.name}
            </span>
          </motion.div>

          <div style={{ textAlign: "left" }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                color: "rgba(255, 255, 255)",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "48px",
                fontWeight: "500",
                marginBottom: "16px",
                lineHeight: "110%",
              }}
            >
              Cadmus is checking
              <div style={{ display: "block", position: "relative", height: "72px" }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentPortal.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    style={{ position: "absolute" }}
                  >
                    <div style={{ display: "inline" }}>
                      {currentPortal.name}
                      <LoadingDots portalName={currentPortal.name} />
                    </div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                      style={{
                        color: "#FFF",
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: "20px",
                        fontWeight: "500",
                        lineHeight: "100%",
                        marginTop: "8px",
                      }}
                    >
                      {currentPortal.ordersText || currentPortal.Message || "No new orders found"}
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const LoadingDots = ({ portalName }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={portalName}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{ display: "inline-flex", marginLeft: "4px" }}
      >
        {[0, 1, 2].map((index) => (
          <motion.span
            key={index}
            style={{
              display: "inline-block",
              width: "9px",
              height: "9px",
              borderRadius: "50%",
              marginRight: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              boxShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
            }}
            initial={index > 0 ? { x: -13 * index } : { x: 0 }}
            animate={{
              x: 0,
              opacity: [0.3, 1, 0.3],
              scale: [0.8, 1, 0.8],
            }}
            transition={{
              x: {
                duration: 0.8,
                ease: [0.68, -0.55, 0.27, 1.55],
                delay: index * 0.1,
              },
              opacity: {
                duration: 1.5,
                repeat: Infinity,
                delay: index * 0.15,
              },
              scale: {
                duration: 1.5,
                repeat: Infinity,
                delay: index * 0.15,
              },
            }}
          />
        ))}
      </motion.span>
    </AnimatePresence>
  );
};

export default PortalActivity;